import { DebugLogger } from '@iconik-io/core/src/debug_logger/debug_logger';

const logger = DebugLogger.getInstance('cutting_room');

const DEFAULT_TIMEOUT_MS = 30_000;
const DEFAULT_READY_TIMEOUT_MS = 30_000;

/**
 * How often a readiness probe is re-sent while none has been answered.
 * CuttingRoom answers a trusted `player_get_status` within milliseconds, so an
 * unanswered one was most likely dropped.
 */
const PROBE_INTERVAL_MS = 250;

/**
 * Outgoing message envelope (host → CuttingRoom).
 */
type CuttingRoomRequest = {
  action: string;
  payload: Record<string, unknown>;
  messageId: string;
};

/**
 * Incoming event envelope (CuttingRoom → host).
 */
export type CuttingRoomResponse = {
  source: 'cuttingroom';
  event: string;
  messageId?: string;
  embedId?: string;
  payload?: Record<string, unknown>;
  response: Record<string, unknown>;
};

type PendingRequest = {
  resolve: (response: CuttingRoomResponse) => void;
  reject: (error: Error) => void;
  timeoutId: ReturnType<typeof setTimeout>;
};

type CuttingRoomEventListener = (event: CuttingRoomResponse) => void;

type Deferred = {
  promise: Promise<void>;
  resolve: () => void;
  reject: (error: Error) => void;
};

function createDeferred(): Deferred {
  let resolve!: () => void;
  let reject!: (error: Error) => void;
  const promise = new Promise<void>((res, rej) => {
    resolve = res;
    reject = rej;
  });
  // The bridge rejects on unregister and on timeout whether or not anyone is
  // awaiting; this keeps that from surfacing as an unhandled rejection. Callers
  // of `waitForReady()` still see the rejection.
  promise.catch(() => {});
  return { promise, resolve, reject };
}

/**
 * Singleton bridge that manages communication with the CuttingRoom iframe.
 *
 * - Holds a reference to the iframe so callers (hooks, thunks) don't need one.
 * - `send()` returns a `Promise` that resolves when the iframe responds with
 *   a matching `messageId`, enabling request/response flows.
 * - `post()` is fire-and-forget for commands that don't need a response.
 * - `subscribe()` lets callers listen to all incoming CuttingRoom events.
 */
class CuttingRoomBridge {
  #iframe: HTMLIFrameElement | null = null;
  #origin: string | null = null;
  #pendingRequests = new Map<string, PendingRequest>();
  #listeners = new Set<CuttingRoomEventListener>();
  #authorized = false;
  #loaded = false;
  #readyDeferred: Deferred = createDeferred();
  #readyTimeoutId: ReturnType<typeof setTimeout> | null = null;
  #readyTimeoutMs = DEFAULT_READY_TIMEOUT_MS;
  #readyStatus: 'pending' | 'ready' | 'timed_out' = 'pending';
  #probeController: AbortController | null = null;

  /**
   * Register the iframe element. Called by the hosting React component on mount.
   * Starts the global `message` listener.
   *
   * If an iframe is already registered, it is unregistered first and a warning
   * is logged.
   */
  registerIframe(
    iframe: HTMLIFrameElement,
    options: { origin: string; readyTimeout?: number },
  ) {
    if (this.#iframe) {
      logger.debug(
        'registerIframe called while an iframe is already registered — unregistering previous iframe',
      );
      this.unregisterIframe();
    }

    this.#iframe = iframe;
    this.#origin = options.origin;
    window.addEventListener('message', this.#handleMessage);

    this.#readyTimeoutMs = options?.readyTimeout ?? DEFAULT_READY_TIMEOUT_MS;
    this.#armReadyTimeout();
  }

  #armReadyTimeout() {
    this.#clearReadyTimeout();
    this.#readyTimeoutId = setTimeout(() => {
      this.#readyTimeoutId = null;
      this.#stopProbing();
      this.#readyStatus = 'timed_out';
      this.#readyDeferred.reject(
        new Error(
          `CuttingRoom iframe did not become ready within ${this.#readyTimeoutMs}ms`,
        ),
      );
    }, this.#readyTimeoutMs);
  }

  #clearReadyTimeout() {
    if (this.#readyTimeoutId) {
      clearTimeout(this.#readyTimeoutId);
      this.#readyTimeoutId = null;
    }
  }

  /**
   * Unregister the iframe. Called on unmount.
   * Rejects all pending requests, rejects the ready promise, clears
   * subscribers, and removes the `message` listener.
   */
  unregisterIframe() {
    this.#iframe = null;
    this.#origin = null;

    for (const [id, pending] of this.#pendingRequests) {
      clearTimeout(pending.timeoutId);
      pending.reject(new Error('CuttingRoom iframe was unregistered'));
      this.#pendingRequests.delete(id);
    }

    this.#clearReadyTimeout();
    this.#stopProbing();
    this.#readyDeferred.reject(
      new Error('CuttingRoom iframe was unregistered'),
    );
    this.#readyDeferred = createDeferred();
    this.#readyStatus = 'pending';

    this.#authorized = false;
    this.#loaded = false;
    this.#listeners.clear();
    window.removeEventListener('message', this.#handleMessage);
  }

  /**
   * Wait until CuttingRoom accepts commands: it has sent both `"authorized"`
   * and `"cuttingroom_loaded"`, and has answered a readiness probe.
   *
   * The probe is needed because CuttingRoom sends `"cuttingroom_loaded"` before
   * it has loaded the origin allow-list it checks commands against, and until
   * then it silently drops them.
   *
   * Rejects if the iframe does not become ready within the timeout configured
   * in `registerIframe`. A call after that deadline has passed starts a new one.
   */
  waitForReady(): Promise<void> {
    if (this.#readyStatus === 'timed_out' && this.#iframe) {
      this.#resetReady();
    }
    return this.#readyDeferred.promise;
  }

  /**
   * Send a message and wait for a correlated response (matched by `messageId`).
   *
   * Usage from a thunk:
   * ```ts
   * const response = await cuttingRoomBridge.send('create_project', { name });
   * ```
   *
   * Aborting `signal` rejects the request and stops listening for its response.
   */
  send(
    action: string,
    payload: Record<string, unknown>,
    options?: { timeout?: number; signal?: AbortSignal },
  ): Promise<CuttingRoomResponse> {
    const iframe = this.#iframe;
    if (!iframe?.contentWindow || !this.#origin) {
      return Promise.reject(new Error('CuttingRoom iframe is not available'));
    }

    const messageId = crypto.randomUUID();
    const timeout = options?.timeout ?? DEFAULT_TIMEOUT_MS;

    const promise = new Promise<CuttingRoomResponse>((resolve, reject) => {
      const timeoutId = setTimeout(() => {
        this.#pendingRequests.delete(messageId);
        reject(
          new Error(
            `CuttingRoom request '${action}' timed out after ${timeout}ms`,
          ),
        );
      }, timeout);

      this.#pendingRequests.set(messageId, { resolve, reject, timeoutId });

      options?.signal?.addEventListener(
        'abort',
        () => {
          clearTimeout(timeoutId);
          this.#pendingRequests.delete(messageId);
          reject(new Error(`CuttingRoom request '${action}' was aborted`));
        },
        { once: true },
      );
    });

    const message: CuttingRoomRequest = { action, payload, messageId };
    iframe.contentWindow.postMessage(message, this.#origin);
    logger.debug('Sent:', message);

    return promise;
  }

  /**
   * Fire-and-forget: send a message without waiting for a response.
   *
   * Pass `redactPayload` for a payload that must not reach the debug log.
   */
  post(
    action: string,
    payload: Record<string, unknown>,
    options?: { redactPayload?: boolean },
  ) {
    const iframe = this.#iframe;
    if (!iframe?.contentWindow || !this.#origin) {
      logger.debug('iframe contentWindow is not available');
      return;
    }

    iframe.contentWindow.postMessage({ action, payload }, this.#origin);
    logger.debug(
      'Posted:',
      options?.redactPayload ? { action } : { action, payload },
    );
  }

  /**
   * Hand CuttingRoom a short-lived Iconik embed token so it can bootstrap a
   * session for the current user instead of showing its own login form.
   *
   * @see https://docs.cuttingroom.com/v1.2.0/reference/embed-token.md
   */
  authorize(embedToken: string) {
    this.post('authorize', { embedToken }, { redactPayload: true });
  }

  /**
   * Subscribe to all incoming CuttingRoom events.
   * Returns an unsubscribe function.
   */
  subscribe(listener: CuttingRoomEventListener): () => void {
    this.#listeners.add(listener);
    return () => {
      this.#listeners.delete(listener);
    };
  }

  #handleMessage = (event: MessageEvent) => {
    if (
      event.source !== this.#iframe?.contentWindow ||
      event.data?.source !== 'cuttingroom'
    ) {
      return;
    }

    if (event.origin !== this.#origin) {
      logger.debug(`Ignored message from unexpected origin: ${event.origin}`);
      return;
    }

    const data = event.data as CuttingRoomResponse;
    logger.debug('Received:', data);

    if (data.event === 'authorized') {
      this.#authorized = true;
      this.#probeIfLoadedAndAuthorized();
    }

    if (data.event === 'cuttingroom_loaded') {
      this.#loaded = true;
      this.#probeIfLoadedAndAuthorized();
    }

    if (data.event === 'unauthorized') {
      this.#authorized = false;
      this.#stopProbing();
      // Only a settled promise needs replacing. Replacing a pending one would
      // orphan the `waitForReady()` callers already awaiting it, which the
      // re-authorize this event triggers is about to satisfy.
      if (this.#readyStatus !== 'pending') {
        this.#resetReady();
      }
    }

    if (data.messageId) {
      const pending = this.#pendingRequests.get(data.messageId);
      if (pending) {
        clearTimeout(pending.timeoutId);
        this.#pendingRequests.delete(data.messageId);
        pending.resolve(data);
      }
    }

    for (const listener of this.#listeners) {
      listener(data);
    }
  };

  #resetReady() {
    this.#readyDeferred = createDeferred();
    this.#readyStatus = 'pending';
    this.#armReadyTimeout();
    this.#probeIfLoadedAndAuthorized();
  }

  #probeIfLoadedAndAuthorized() {
    if (
      this.#authorized &&
      this.#loaded &&
      this.#readyStatus === 'pending' &&
      !this.#probeController
    ) {
      this.#startProbing();
    }
  }

  /**
   * Send `player_get_status` every `PROBE_INTERVAL_MS` until CuttingRoom answers
   * one. Every probe keeps listening until the ready deadline, so a slow answer
   * still counts.
   *
   * Only the probe is repeated: a dropped command looks the same as a slow one
   * from here, so repeating something like `POST /projects` could duplicate it.
   */
  #startProbing() {
    const controller = new AbortController();
    const { signal } = controller;
    this.#probeController = controller;

    const sendProbe = () => {
      this.send(
        'player_get_status',
        {},
        { timeout: this.#readyTimeoutMs, signal },
      ).then(
        () => {
          if (signal.aborted) {
            return;
          }
          logger.debug('Readiness probe answered; CuttingRoom is ready');
          this.#stopProbing();
          this.#clearReadyTimeout();
          this.#readyStatus = 'ready';
          this.#readyDeferred.resolve();
        },
        // An unanswered probe is reported by the ready deadline instead.
        () => {},
      );
    };

    const intervalId = setInterval(sendProbe, PROBE_INTERVAL_MS);
    signal.addEventListener('abort', () => clearInterval(intervalId), {
      once: true,
    });
    sendProbe();
  }

  #stopProbing() {
    this.#probeController?.abort();
    this.#probeController = null;
  }
}

export const cuttingRoomBridge = new CuttingRoomBridge();
